import { AboutPost } from '../EditPostMenu/AboutPost'
import { EditPostMenu } from '../EditPostMenu/EditPostMenu'
import { ScheduledPostTargetsPreview } from 'components/ScheduledPostTargetsPreview'
import { ScheduledPostShow } from 'api/clients/ScheduledPostsClient'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { filterGBPFromCrossPostGroup, getCrossPostGroupForChannel, isVideoChannel } from '../utils'
import Styles from './Toolbar.module.scss'
import { PostStatusPropertyValue } from 'api/clients/PropertyClient'
import { ToolbarMenu } from './ToolbarMenu'
import { PostingOnMenu } from './PostingOnMenu'
import { CalendarFilters } from 'screens/Calendar/types'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { DesignChannel } from 'components/NewFileModal/Types'
import { GenerationType } from 'components/BlazeCommandLine/api/createDocGenerationRequest'
import { PostNotificationBanner } from './PostNotificationBanner'
import { WhatsNewBanner } from './WhatsNewBanner'
import cx from 'classnames'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { AutopilotUpgradeRemoveWatermarkButton } from 'components/AutopilotUpgradeButtons/AutopilotUpgradeRemoveWatermarkButton'
import { AlmanacFileShowItem } from 'hooks/queries/files/types'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { PostApproval } from '../PostApproval/PostApproval'
import { NavigationButtons } from './NavigationButtons'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'
import { BlogPostLearnMoreLink } from 'components/ArticlePostPreview/BlogPostLearnMoreLink/BlogPostLearnMoreLink'
import { ContentRating } from './ContentRating'

export type EditPostMenuState = {
  type: 'document' | 'blank' | 'default'
  isBlankDesign: boolean
}

interface ToolbarProps {
  scheduledPost: ScheduledPostShow
  almanacFile: AlmanacFileShowItem
  previewPlatform?: ExternalIntegrationEnum
  onSelectPlatform: (platform: ExternalIntegrationEnum) => void
  isBlankDesign: boolean
  onLoadingChange?: (isLoading: boolean) => void
  isLoading: boolean
  setIsLoading: (isLoading: boolean) => void
  onDelete: (skipNavigation?: boolean) => void
  filters: CalendarFilters
  fileChannel: ChannelType | DesignChannel | GenerationType
  startDate?: string
  // Animation props
  animationState?: 'idle' | 'animating'
  targetEditPostMenuState?: EditPostMenuState | null
  onUpdateAlmanacFile?: (updatedFile: AlmanacFileShowItem) => void
  isCurrentChannelUnsupported?: boolean
  onClose?: () => void
  showNavigation?: boolean
  goToPreviousFile?: () => void
  goToNextFile?: () => void
}

export const Toolbar = ({
  scheduledPost,
  almanacFile,
  previewPlatform,
  onSelectPlatform,
  isBlankDesign,
  onLoadingChange,
  isLoading,
  setIsLoading,
  onDelete,
  filters,
  fileChannel,
  startDate,
  animationState = 'idle',
  targetEditPostMenuState,
  onUpdateAlmanacFile,
  isCurrentChannelUnsupported = false,
  onClose,
  showNavigation = false,
  goToPreviousFile,
  goToNextFile,
}: ToolbarProps) => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const showVideoCrossPost = isVideoChannel(fileChannel as DesignChannel)
  const isGBPEnabled = useFlipperFlag('google_business_profile_integration')
  const crossPostGroup = filterGBPFromCrossPostGroup(getCrossPostGroupForChannel(fileChannel), isGBPEnabled)
  const postApprovalsEnabled = useFlipperFlag('post_approvals')
  const isRefineAIGenerationEnabled = useFlipperFlag('refine_ai_generation')
  const workspace = useCurrentWorkspace()

  return (
    <div className={Styles.toolbar}>
      {postApprovalsEnabled && showNavigation && (
        <NavigationButtons goToPreviousFile={goToPreviousFile} goToNextFile={goToNextFile} />
      )}

      <div className={Styles.innerToolbar}>
        {postApprovalsEnabled && (
          <PostApproval
            almanacFile={almanacFile}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            onDelete={() => onDelete(true)}
            onUpdateAlmanacFile={onUpdateAlmanacFile}
            filters={filters}
            fileChannel={fileChannel}
            startDate={startDate}
          />
        )}
        {almanacFile.completed_ai_suggestion_request?.id && !almanacFile.completed_ai_suggestion_request?.rated && (
          <div className={Styles.toolbarItem}>
            <ContentRating subjectId={almanacFile.completed_ai_suggestion_request.id} fileKey={almanacFile.key} />
          </div>
        )}
        {isRefineAIGenerationEnabled && almanacFile.asset_type === 'Design' && almanacFile.source_media_info && (
          <div className={Styles.toolbarItem}>
            <AboutPost sourceMediaInfo={almanacFile.source_media_info} />
          </div>
        )}
        <div
          className={cx(Styles.toolbarItem, Styles.editPostMenuContainer, {
            [Styles.hasNextSection]: false,
          })}
        >
          <EditPostMenu
            scheduledPost={scheduledPost}
            almanacFile={almanacFile}
            isBlankDesign={isBlankDesign}
            onLoadingChange={onLoadingChange}
            animationState={animationState}
            targetState={targetEditPostMenuState}
            onClose={onClose}
          />
        </div>
        <div className={Styles.toolbarItem}>
          <ToolbarMenu title="Posting on" height={isSystemRedesignEnabled ? 20 : undefined}>
            <PostingOnMenu
              almanacFile={almanacFile}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              onDelete={() => onDelete(true)}
              filters={filters}
              fileChannel={fileChannel}
              startDate={startDate}
              onUpdateAlmanacFile={onUpdateAlmanacFile}
              buttonSize={isSystemRedesignEnabled ? 'md' : undefined}
            />
          </ToolbarMenu>
        </div>
        {!isCurrentChannelUnsupported && (
          <div
            className={cx(Styles.toolbarItem, Styles.scheduledPostTargetsContainer)}
            data-animation-state={animationState}
          >
            <ScheduledPostTargetsPreview
              almanacFile={almanacFile}
              activePlatform={previewPlatform || undefined}
              onSelectPlatform={onSelectPlatform}
              showVideoCrossPost={showVideoCrossPost}
              status={almanacFile.post_status as PostStatusPropertyValue}
              calendarItemKey={almanacFile.key}
              filters={filters}
              crossPostGroup={crossPostGroup}
              onUpdateAlmanacFile={onUpdateAlmanacFile}
            />
          </div>
        )}
        {workspace?.is_ap_freemium && (
          <div className={Styles.toolbarItem}>
            <AutopilotUpgradeRemoveWatermarkButton />
          </div>
        )}

        <PostNotificationBanner scheduledPost={scheduledPost} />

        {almanacFile.channel === ChannelType.BlogPost && (
          <div className={Styles.toolbarItem}>
            <BlogPostLearnMoreLink />
          </div>
        )}

        <div className={Styles.toolbarItem}>
          <WhatsNewBanner />
        </div>
      </div>
    </div>
  )
}
